/**
 * WalletHome — Home / Passport Dashboard
 *
 * Two states, one experience (per the design spec):
 *   A. Brand-new user (first login after setup)
 *      • "Let's get started" carousel (Complete Financial Profile,
 *        Add first financial record)
 *      • Financial Life Map with "No records" chips
 *      • Trusted Institutions empty state ("You haven't connected …")
 *   B. Established user
 *      • Attention area — top 1-3 dynamic items (Passport reviews,
 *        product reviews, understanding checks)
 *      • Financial Life Map with counts per core area
 *      • Trusted Institutions cards with "Last interaction"
 *
 * The state is detected off existing derived data — no new backend
 * calls needed. Both states share the same "What do you want to do?"
 * action grid and the same inline "Ask NAVI" panel.
 */
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  ArrowRight, ChevronLeft, ChevronRight, Search, Bell, Check,
  Landmark, PiggyBank, LineChart, ShieldCheck, CreditCard, ScrollText, Anchor,
  FileText, Compass, AlertTriangle, HelpCircle,
  Building2, Sparkles, Gavel,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { useWalletDerived } from "@/lib/wallet-journeys";
import { loadWallet } from "@/lib/wallet-storage";
import { usePassportReviewItems, formatAge } from "@/lib/wallet-passport-review";
import { useNotificationsFeed } from "@/lib/wallet-notifications";
import { useWalletDecisions, DECISION_STATUS, zar } from "@/lib/wallet-decisions";
import NaviFace from "@/components/wallet/NaviFace";
import CommandPalette from "@/components/wallet/CommandPalette";
import NotificationsPanel from "@/components/wallet/NotificationsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/** Human-friendly time-of-day greeting. */
function greetingFor(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** 7 core Financial Life Map categories in the order the spec shows them. */
const LIFE_MAP = [
  { key: "banking",     label: "Banking",         icon: Landmark,   types: ["current_account","cheque_account","bank_account"] },
  { key: "savings",     label: "Savings",         icon: PiggyBank,  types: ["savings_account","fixed_deposit","tax_free_savings"] },
  { key: "investments", label: "Investments",     icon: LineChart,  types: ["unit_trust","share_portfolio","etf","offshore_investment","investment"] },
  { key: "retirement",  label: "Retirement",      icon: Anchor,     types: ["pension","retirement_annuity","provident_fund","preservation_fund"] },
  { key: "insurance",   label: "Insurance",       icon: ShieldCheck, types: ["life_insurance","risk_cover","medical_aid","gap_cover","other_insurance","funeral_cover"] },
  { key: "credit",      label: "Credit & Debt",   icon: CreditCard, types: ["home_loan","vehicle_finance","personal_loan","credit_card","store_card","student_loan"] },
  { key: "estate",      label: "Estate Planning", icon: ScrollText, types: ["will","trust","estate_document","power_of_attorney"] },
];

export default function WalletHome() {
  const nav = useNavigate();
  const { view } = useWalletDerived();
  const passport = usePassportReviewItems();
  const decisions = useWalletDecisions();
  const [wallet, setWallet] = useState(null);
  const [profileComplete, setProfileComplete] = useState(null);

  useEffect(() => {
    const w = loadWallet();
    if (!w || !w.setup_completed_at) { nav("/wallet/onboarding", { replace: true }); return; }
    setWallet(w);
    if (w.device_id) {
      axios.get(`${API}/wallet/financial-profile`, { params: { device_id: w.device_id } })
        .then(r => setProfileComplete(Boolean(r.data?.profile?.completed_at)))
        .catch(() => setProfileComplete(false));
    } else setProfileComplete(false);
  }, [nav]);

  // Derive counts + attention items
  const counts = useMemo(() => {
    const map = {};
    for (const c of LIFE_MAP) map[c.key] = 0;
    if (!view) return map;
    const all = [...(view.active || []), ...(view.considering || [])];
    for (const row of all) {
      const t = (row.product_type || "").toLowerCase();
      const cat = LIFE_MAP.find(c => c.types.includes(t));
      if (cat) map[cat.key] += 1;
    }
    return map;
  }, [view]);

  const totalRecords = Object.values(counts).reduce((a, b) => a + b, 0);
  const institutions = view?.institutions || [];

  // Attention items — up to 3 (TechSprint demo · Nomsa's 3 investment products at different stages · fictional test institutions only)
  const attention = useMemo(() => [
    {
      kind: "product",
      title: "ABC Wealth · Balanced Growth Portfolio",
      detail: "New proposal from James Smith — verify the sender before you review the details.",
      when: "Trust check pending",
      to: "/wallet/decisions",
      icon: Gavel,
    },
    {
      kind: "product",
      title: "Horizon Capital · Income Tracker Fund",
      detail: "You've verified the sender. Continue with NAVI to understand the product.",
      when: "Understanding in progress",
      to: "/wallet/decisions",
      icon: FileText,
    },
    {
      kind: "product",
      title: "Meridian Asset Managers · Global Equity Fund",
      detail: "All three checks complete. Ready to record your decision.",
      when: "Ready to decide",
      to: "/wallet/decisions",
      icon: Check,
    },
  ], []);

  // State detection — "brand-new" = no records + no institutions + profile not done
  const brandNew = totalRecords === 0 && institutions.length === 0;

  const trustPending = decisions.trustPending || [];
  const otherActive = (decisions.active || []).filter(d => d.status !== DECISION_STATUS.TRUST);

  return (
    <WalletShell activeTab="home">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="wallet-home">
        <TopBar />

        <PersonalGreeting name={wallet?.name} brandNew={brandNew} attentionCount={attention.length + trustPending.length} />

        <DecisionNudges trustPending={trustPending} otherActive={otherActive} />

        {brandNew ? (
          <LetsGetStarted profileComplete={profileComplete} />
        ) : null}

        {attention.length > 0 ? (
          <>
            <AttentionArea items={attention} />
            <NaviWillCallout />
          </>
        ) : null}

        <QuickActions />

        <TrustedInstitutionsCard institutions={institutions} />

        <AskNaviInline />
      </div>
    </WalletShell>
  );
}

/* ─────────────────────────── Top bar ──────────────────────────── */
function TopBar() {
  const wallet = loadWallet() || {};
  const initial = (wallet.name || "N").charAt(0).toUpperCase();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { items, unreadCount, markRead, markAllRead } = useNotificationsFeed();

  // Cmd/Ctrl+K anywhere to open the palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="flex items-center justify-between relative" data-testid="home-topbar">
      <Link to="/wallet" className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: NAVY }}>A</div>
        <span className="font-display font-semibold text-lg" style={{ color: NAVY }}>Altara</span>
      </Link>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCmdOpen(true)}
          aria-label="Search"
          data-testid="home-search"
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition"
          title="Search  (Ctrl+K)"
        >
          <Search className="w-4 h-4 text-slate-500" />
        </button>
        <div className="relative">
          <button
            onClick={() => setNotifOpen(o => !o)}
            aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
            data-testid="home-bell"
            aria-expanded={notifOpen}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition relative"
          >
            <Bell className="w-4 h-4 text-slate-500" />
            {unreadCount > 0 && (
              <span
                data-testid="home-bell-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-mono font-bold flex items-center justify-center text-white"
                style={{ background: GOLD, color: NAVY }}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          <NotificationsPanel
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            items={items}
            markRead={markRead}
            markAllRead={markAllRead}
          />
        </div>
        <Link to="/wallet/settings" data-testid="home-avatar" className="w-9 h-9 rounded-full flex items-center justify-center font-display font-semibold text-white" style={{ background: NAVY }}>{initial}</Link>
      </div>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </header>
  );
}

/* ─────────────────── Personal greeting + subtitle ─────────────── */
function PersonalGreeting({ name, brandNew, attentionCount }) {
  // TechSprint demo persona — greeting always shows Nomsa.
  const firstName = "Nomsa";
  const greeting = greetingFor();
  const subtitle = brandNew
    ? { headline: "Your Trust Wallet is ready.", body: "Start building a clearer picture of your financial life." }
    : attentionCount > 0
      ? { headline: "You're on top of things.", body: "Here's what's important today." }
      : { headline: "You're all caught up.", body: "Nothing needs your attention right now." };
  return (
    <section data-testid="home-greeting">
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        {greeting}, {firstName}
      </h1>
      <div className="mt-1 text-[13.5px] text-slate-700 leading-relaxed">
        <div className="font-medium" style={{ color: NAVY }}>{subtitle.headline}</div>
        <div className="text-slate-500">{subtitle.body}</div>
      </div>
    </section>
  );
}

/* ─────────────── State A · Let's get started carousel ──────────── */
function LetsGetStarted({ profileComplete }) {
  const [i, setI] = useState(0);
  const cards = [
    profileComplete === false && {
      key: "profile",
      icon: FileText,
      title: "Complete your Financial Profile",
      duration: "~2 min",
      body: "Give Altara some basic context to make future information more relevant.",
      cta: "Continue",
      to: "/wallet/profile",
    },
    {
      key: "record",
      icon: FileText,
      title: "Add your first financial record",
      duration: "~2 min",
      body: "Upload an insurance policy, investment statement, retirement document or other financial record.",
      cta: "Add a record",
      to: "/wallet/documents",
    },
  ].filter(Boolean);
  if (cards.length === 0) return null;
  const total = cards.length;
  const active = cards[i];
  return (
    <section data-testid="home-lets-get-started">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>Let's get started</h2>
        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span>{i + 1} of {total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setI(i => Math.max(0, i - 1))} disabled={i === 0} data-testid="lgs-prev" className="w-6 h-6 rounded-full flex items-center justify-center border border-slate-200 disabled:opacity-30"><ChevronLeft className="w-3 h-3" /></button>
            <button onClick={() => setI(i => Math.min(total - 1, i + 1))} disabled={i === total - 1} data-testid="lgs-next" className="w-6 h-6 rounded-full flex items-center justify-center border border-slate-200 disabled:opacity-30"><ChevronRight className="w-3 h-3" /></button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c, idx) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`lgs-card-${c.key}`}
            className={`rounded-2xl bg-white border p-4 flex flex-col ${idx === i ? "border-slate-300 shadow-sm" : "border-slate-100 opacity-90"}`}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${GOLD}22` }}>
              <c.icon className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div className="font-display text-[14px] font-semibold" style={{ color: NAVY }}>{c.title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{c.duration}</div>
            <p className="text-[12px] text-slate-600 mt-2 leading-relaxed flex-1">{c.body}</p>
            <button
              className="mt-4 inline-flex items-center justify-center gap-1 px-4 py-2 rounded-xl text-[12px] font-semibold"
              style={{ background: idx === i ? NAVY : "#E2E8F0", color: idx === i ? "white" : "#475569" }}
            >
              {c.cta}
            </button>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─────────────── Decision nudges (TechSprint proposals) ────────── */
function DecisionNudges({ trustPending, otherActive }) {
  if (!trustPending.length && !otherActive.length) return null;
  return (
    <section data-testid="home-decision-nudges" className="space-y-3">
      {trustPending.map(d => (
        <Link
          key={d.decision_id}
          to={`/wallet/decisions/${d.decision_id}`}
          data-testid={`decision-nudge-${d.decision_id}`}
          className="block rounded-2xl border p-4 lg:p-5 hover:shadow-md transition"
          style={{ background: "linear-gradient(135deg, #FFFDF6 0%, #FFF6E0 100%)", borderColor: "#F0D689" }}
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: NAVY }}>
              <Gavel className="w-5 h-5" style={{ color: GOLD }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
                New decision · Trust check needed
              </div>
              <div className="mt-1 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
                {d.institution_name} sent you an investment proposal
              </div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                <span className="font-semibold" style={{ color: NAVY }}>{d.proposal_name}</span> · {zar(d.financial_commitment_zar)} over {d.investment_term_months} months.
                Confirm who sent it before you consider the details.
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white" style={{ background: NAVY }}>
                Open trust check <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </Link>
      ))}
      {otherActive.length > 0 && !trustPending.length && (
        <Link
          to="/wallet/decisions"
          data-testid="decision-nudge-other-active"
          className="rounded-2xl bg-white border border-slate-100 hover:shadow-sm transition p-4 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
            <Gavel className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
              {otherActive.length} decision{otherActive.length === 1 ? "" : "s"} in progress
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">Continue where you left off.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </Link>
      )}
    </section>
  );
}

/* ─────────────── State B · Attention area (top 1-3) ────────────── */
function AttentionArea({ items }) {
  return (
    <section data-testid="home-attention">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
          {items.length} thing{items.length === 1 ? "" : "s"} need your attention
        </h2>
        <Link to="/wallet/documents" className="text-[12px] font-medium hover:underline" style={{ color: NAVY }}>View all</Link>
      </div>
      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        {items.map((it, idx) => (
          <Link
            key={idx}
            to={it.to}
            data-testid={`attention-${idx}`}
            className={`flex items-start gap-3 p-4 hover:bg-slate-50 transition ${idx < items.length - 1 ? "border-b border-slate-100" : ""}`}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <it.icon className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>{it.title}</div>
              <div className="text-[11.5px] text-slate-600 mt-0.5 leading-snug">{it.detail}</div>
            </div>
            <div className="text-[10.5px] text-slate-400 uppercase tracking-widest flex-shrink-0">{it.when}</div>
            <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-1" />
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─────────────── NAVI callout · missing will (TechSprint) ─────── */
function NaviWillCallout() {
  return (
    <section data-testid="home-navi-will-callout">
      <div
        className="rounded-2xl p-4 lg:p-5 border flex items-start gap-3"
        style={{ background: "linear-gradient(135deg, #FFFDF6 0%, #FFF6E0 100%)", borderColor: "#F0D689" }}
      >
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: NAVY }}>
          <Sparkles className="w-5 h-5" style={{ color: GOLD }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            NAVI · a gentle nudge
          </div>
          <div className="mt-1 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
            Nomsa, you don&apos;t have a will on file yet.
          </div>
          <div className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
            You&apos;re about to invest R450 000. A will is what makes sure that money
            reaches the people you love if anything happens. It usually takes an hour
            with a professional — I can explain what&apos;s involved.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/wallet/documents"
              data-testid="navi-will-upload"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              I already have one — upload it <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/wallet/assistance"
              data-testid="navi-will-learn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold border"
              style={{ background: "white", borderColor: "#F0D689", color: NAVY }}
            >
              Tell me what a will is
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────── Quick Actions ─────────────────────────── */
function QuickActions() {
  const actions = [
    { key: "understand", label: "Understand a document",  icon: FileText,      to: "/wallet/documents",  tone: `${NAVY}0F` },
    { key: "review",     label: "Review a financial decision", icon: Compass,   to: "/wallet/decisions",  tone: `${GOLD}22` },
    { key: "suspicious", label: "Check something suspicious",  icon: AlertTriangle, to: "/wallet/safety", tone: "#FCE7F3" },
    { key: "help",       label: "Get help",               icon: HelpCircle,    to: "/wallet/assistance", tone: "#DBEAFE" },
  ];
  return (
    <section data-testid="home-quick-actions">
      <h2 className="font-display text-[15px] font-semibold mb-3" style={{ color: NAVY }}>What do you want to do?</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {actions.map(a => (
          <Link
            key={a.key}
            to={a.to}
            data-testid={`quick-${a.key}`}
            className="rounded-2xl bg-white border border-slate-100 hover:shadow-sm transition p-4 flex flex-col items-center text-center"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ background: a.tone }}>
              <a.icon className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div className="text-[11.5px] font-medium leading-tight" style={{ color: NAVY }}>{a.label}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────── Trusted Institutions card ─────────────────── */
function TrustedInstitutionsCard({ institutions }) {
  const isEmpty = institutions.length === 0;
  return (
    <section data-testid="home-institutions">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>My Trusted Institutions</h2>
        <Link to="/wallet/institutions" className="text-[12px] font-medium hover:underline" style={{ color: NAVY }}>
          {isEmpty ? " " : "Manage"}
        </Link>
      </div>
      {isEmpty ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-5 flex items-center gap-4" data-testid="institutions-empty">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#F1F5F9" }}>
            <Building2 className="w-5 h-5 text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-slate-700 leading-relaxed">
              You haven't connected a participating financial institution yet.
            </div>
            <Link
              to="/wallet/institutions"
              data-testid="institutions-explore"
              className="mt-3 inline-flex items-center gap-1 rounded-full px-4 py-2 text-[12px] font-semibold"
              style={{ background: `${GOLD}22`, color: NAVY }}
            >
              Explore Trusted Institutions <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {institutions.slice(0, 3).map((i) => (
            <Link
              key={i.key || i.id}
              to={`/wallet/institutions`}
              data-testid={`institution-${i.key || i.id}`}
              className="rounded-2xl bg-white border border-slate-100 hover:shadow-sm transition p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold text-[11px]" style={{ background: `${GOLD}22`, color: NAVY }}>
                  {(i.name || i.key || "?").charAt(0).toUpperCase()}
                </div>
                <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>{i.name || i.key}</div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{(i.product_types || []).slice(0, 2).map(t => t.replace(/_/g, " ")).join(" · ") || "Institution"}</div>
              <div className="text-[10.5px] text-slate-400 mt-1">Last interaction: {i.last_interaction || "recently"}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────── Ask NAVI inline row ───────────────────── */
function AskNaviInline() {
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent("navi.open"))}
      data-testid="home-ask-navi"
      className="w-full rounded-2xl border border-slate-100 bg-white hover:shadow-sm transition p-4 flex items-center gap-3 text-left"
    >
      <NaviFace size="md" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Ask NAVI</div>
        <div className="text-[11.5px] text-slate-500 leading-snug">
          I can help explain financial information, documents and features in your Passport.
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}
